import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { TopNav } from "@/components/TopNav";
import { api, wsUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Target, Plus, Minus, Trophy, X, History, Play, StopCircle, Search, Users, Copy, KeyRound, LogIn } from "lucide-react";
import { toast } from "sonner";

export default function PointCounter() {
  const { id } = useParams();
  if (id) return <SessionView sid={id} />;
  return <Lobby />;
}

const Lobby = () => {
  const nav = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [creating, setCreating] = useState(false);
  const [joinCode, setJoinCode] = useState("");

  const load = () => api.get("/point-sessions?status=active").then(({ data }) => setSessions(data));
  useEffect(() => {
    load();
    const i = setInterval(load, 6000);
    // Auto-join via URL ?join=CODE
    const params = new URLSearchParams(window.location.search);
    const code = params.get("join");
    if (code) {
      api.post("/point-sessions/join", { room_code: code.toUpperCase() })
        .then(({ data }) => { toast.success("Đã tham gia phòng"); nav(`/point-counter/${data.id}`); })
        .catch(() => toast.error("Không tìm thấy phòng"));
    }
    return () => clearInterval(i);
  }, [nav]);

  const joinByCode = async (e) => {
    e?.preventDefault();
    const code = joinCode.trim().toUpperCase();
    if (code.length !== 6) return toast.error("Mã phòng 6 ký tự");
    try {
      const { data } = await api.post("/point-sessions/join", { room_code: code });
      toast.success("Đã tham gia phòng");
      nav(`/point-counter/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Không tìm thấy phòng"); }
  };

  return (
    <div className="min-h-screen bg-[#05050a]">
      <TopNav />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Target className="text-yellow-300" />
            <h1 className="font-display font-bold text-3xl sm:text-4xl">Bắn đền</h1>
          </div>
          <button onClick={() => setCreating(true)} data-testid="new-session-btn" className="px-4 py-2.5 rounded-md text-sm font-medium bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500 text-black flex items-center gap-2">
            <Plus size={15} /> Tạo phiên mới
          </button>
        </div>

        {/* Join by room code */}
        <form onSubmit={joinByCode} className="rounded-xl bg-gradient-to-br from-amber-500/10 to-yellow-700/5 border border-amber-400/30 p-4 mb-6 flex flex-col sm:flex-row items-stretch gap-3">
          <div className="flex items-center gap-2 sm:flex-1">
            <KeyRound className="text-amber-300" size={18} />
            <div>
              <div className="text-xs uppercase tracking-wider text-amber-300 font-display font-semibold">Tham gia phòng có sẵn</div>
              <div className="text-[11px] text-white/50">Nhập mã 6 ký tự bạn nhận được từ chủ phòng</div>
            </div>
          </div>
          <input
            value={joinCode}
            onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
            maxLength={6}
            data-testid="room-code-input"
            placeholder="ABC123"
            className="bg-black/40 border border-amber-400/30 rounded-md px-4 py-2.5 text-base font-display font-bold tracking-[0.4em] text-amber-300 text-center outline-none focus:border-amber-400/70 w-full sm:w-44"
          />
          <button type="submit" data-testid="join-room-btn" className="px-5 py-2.5 rounded-md text-sm font-display font-semibold bg-amber-400/15 border border-amber-400/40 text-amber-300 hover:bg-amber-400/25 flex items-center justify-center gap-2">
            <LogIn size={14} /> Vào phòng
          </button>
        </form>

        <h2 className="font-display font-semibold text-sm uppercase tracking-wider text-white/60 mb-3">Phiên đang chạy</h2>
        {sessions.length === 0 ? (
          <div className="rounded-xl bg-[#0c0d14] border border-white/8 p-12 text-center text-white/40">
            Không có phiên nào đang chạy. Tạo phiên để bắt đầu tính điểm bắn đền!
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {sessions.map((s) => {
              const leader = [...s.players].sort((a, b) => b.current_score - a.current_score)[0];
              return (
                <Link key={s.id} to={`/point-counter/${s.id}`} className="rounded-xl bg-[#0c0d14] border border-white/8 p-4 hover:border-amber-400/40 transition" data-testid={`session-${s.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-xs text-amber-300 uppercase tracking-wider">{s.table_number ? `Bàn ${s.table_number}` : "Không bàn"}</div>
                    {s.room_code && (
                      <span className="text-[10px] font-display font-bold tracking-[0.2em] text-amber-300 bg-amber-400/10 px-2 py-0.5 rounded border border-amber-400/30">
                        {s.room_code}
                      </span>
                    )}
                  </div>
                  <div className="font-display font-semibold mb-2">{s.session_name}</div>
                  <div className="flex items-center gap-2 text-xs text-white/60 mb-3"><Users size={11} /> {s.players.length} người chơi</div>
                  {leader && (
                    <div className="flex items-center gap-2 p-2 rounded bg-white/[0.03] border border-white/5">
                      <Trophy size={12} className="text-yellow-400" />
                      <span className="text-xs flex-1 truncate">{leader.nickname}</span>
                      <span className="font-display font-bold text-yellow-300">{leader.current_score}</span>
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        )}

        <Link to="/point-counter-history" className="mt-6 inline-flex items-center gap-2 text-sm text-cyan-300 hover:underline">
          <History size={14} /> Lịch sử điểm bắn đền
        </Link>
      </div>

      {creating && <CreateModal onClose={() => setCreating(false)} onDone={(sid) => nav(`/point-counter/${sid}`)} />}
    </div>
  );
};

const CreateModal = ({ onClose, onDone }) => {
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [tableId, setTableId] = useState("");
  const [selected, setSelected] = useState([user?.id].filter(Boolean));
  const [players, setPlayers] = useState([]);
  const [tables, setTables] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/players?limit=500").then(({ data }) => setPlayers(data));
    api.get("/tables").then(({ data }) => setTables(data));
  }, []);

  const toggle = (pid) => setSelected(selected.includes(pid) ? selected.filter((x) => x !== pid) : [...selected, pid]);

  const submit = async () => {
    if (selected.length < 1) return toast.error("Chọn ít nhất 1 người");
    try {
      const { data } = await api.post("/point-sessions", { session_name: name || undefined, table_id: tableId || undefined, player_ids: selected });
      toast.success("Đã tạo phiên");
      onDone(data.id);
    } catch (e) { toast.error(e?.response?.data?.detail); }
  };

  const filtered = players.filter((p) => p.nickname.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-xl rounded-xl bg-[#0c0d14] border border-white/10 p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-semibold">Tạo phiên Bắn đền</h3>
          <button onClick={onClose} className="p-1 hover:bg-white/5 rounded"><X size={16} /></button>
        </div>
        <label className="block mb-3">
          <span className="text-[10px] uppercase tracking-wider text-white/50">Tên phiên</span>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="vd: Bắn đền tối thứ 7" className="mt-1 w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm outline-none focus:border-cyan-400/60" />
        </label>
        <label className="block mb-4">
          <span className="text-[10px] uppercase tracking-wider text-white/50">Bàn (tùy chọn)</span>
          <select value={tableId} onChange={(e) => setTableId(e.target.value)} className="mt-1 w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm">
            <option value="">-- Không bàn --</option>
            {tables.map((t) => <option key={t.id} value={t.id}>Bàn {t.number}</option>)}
          </select>
        </label>
        <div className="relative mb-2">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tìm người chơi..." className="w-full bg-white/[0.03] border border-white/10 rounded pl-9 pr-3 py-2 text-sm outline-none focus:border-cyan-400/60" />
        </div>
        <div className="rounded-md bg-white/[0.02] border border-white/10 p-2 max-h-60 overflow-y-auto mb-2" data-testid="player-picker">
          {filtered.map((p) => (
            <button key={p.id} onClick={() => toggle(p.id)} type="button"
              className={`w-full flex items-center gap-2 p-1.5 rounded text-left ${selected.includes(p.id) ? "bg-cyan-400/15 text-cyan-300" : "hover:bg-white/5"}`}>
              <img src={p.avatar_url} alt="" className="w-6 h-6 rounded-full" />
              <span className="text-xs flex-1 truncate">{p.nickname}</span>
              {selected.includes(p.id) && <span className="text-[10px] text-cyan-300">✓ #{selected.indexOf(p.id) + 1}</span>}
            </button>
          ))}
        </div>
        <div className="text-xs text-white/50 mb-4">{selected.length}/8 người · Tối đa 8</div>
        <button onClick={submit} disabled={selected.length === 0 || selected.length > 8} data-testid="submit-session" className="w-full py-2.5 rounded-md font-display font-semibold text-sm bg-gradient-to-r from-cyan-400 to-purple-500 text-black disabled:opacity-50 flex items-center justify-center gap-2">
          <Play size={15} /> Bắt đầu phiên
        </button>
      </div>
    </div>
  );
};

const SessionView = ({ sid }) => {
  const nav = useNavigate();
  const { user } = useAuth();
  const [s, setS] = useState(null);

  const load = () => api.get(`/point-sessions/${sid}`).then(({ data }) => setS(data));

  useEffect(() => {
    load();
    let ws;
    try {
      ws = new WebSocket(wsUrl("/api/ws/tv"));
      ws.onmessage = (e) => {
        try {
          const ev = JSON.parse(e.data);
          if (ev.session?.id === sid) setS(ev.session);
        } catch {}
      };
    } catch {}
    return () => { if (ws) ws.close(); };
    // eslint-disable-next-line
  }, [sid]);

  const score = async (pid, delta) => {
    try {
      await api.post(`/point-sessions/${sid}/score`, { player_id: pid, delta });
    } catch (e) { toast.error(e?.response?.data?.detail); }
  };

  const endSession = async () => {
    if (!window.confirm("Kết thúc phiên? Điểm sẽ được lưu vào hồ sơ và quy đổi coin.")) return;
    try {
      const { data } = await api.post(`/point-sessions/${sid}/end`);
      toast.success(`Đã kết thúc. ${data.transactions.length} người nhận coin.`);
      nav("/point-counter");
    } catch (e) { toast.error(e?.response?.data?.detail); }
  };

  if (!s) return <div className="min-h-screen bg-[#05050a]"><TopNav /><div className="p-10 text-white/40">Đang tải...</div></div>;

  // Keep original join order — do NOT auto-reorder by score.
  const players = s.players;
  const maxScore = players.reduce((m, p) => Math.max(m, p.current_score), -Infinity);
  const isActive = s.status === "active";
  const canScore = user?.role === "admin" || user?.role === "staff" || s.created_by === user?.id || s.players.some((p) => p.player_id === user?.id);
  const inviteUrl = s.room_code ? `${window.location.origin}/point-counter?join=${s.room_code}` : null;

  const copyCode = () => {
    if (!s.room_code) return;
    navigator.clipboard?.writeText(s.room_code);
    toast.success(`Đã copy mã: ${s.room_code}`);
  };

  return (
    <div className="min-h-screen bg-[#05050a]">
      <TopNav />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between mb-5 gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-amber-300">{s.table_number ? `Bàn ${s.table_number}` : "Phiên bắn đền"}</div>
            <h1 className="font-display font-bold text-2xl sm:text-3xl">{s.session_name}</h1>
          </div>
          {isActive && canScore && (
            <button onClick={endSession} data-testid="end-session" className="px-4 py-2.5 rounded-md text-sm bg-red-500/15 border border-red-500/40 text-red-300 hover:bg-red-500/25 flex items-center gap-2">
              <StopCircle size={15} /> Kết thúc phiên
            </button>
          )}
        </div>

        {/* Room code invite banner */}
        {isActive && s.room_code && (
          <div className="rounded-xl bg-gradient-to-r from-amber-500/15 to-yellow-700/5 border border-amber-400/40 p-4 mb-5 flex items-center gap-4">
            <KeyRound size={22} className="text-amber-300 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-amber-300 font-display font-semibold">Mời người khác tham gia</div>
              <div className="text-xs text-white/60">Đưa mã này cho bạn bè để họ join cùng phiên</div>
            </div>
            <div className="font-display font-black text-2xl sm:text-3xl tracking-[0.3em] text-amber-300" data-testid="room-code-display">
              {s.room_code}
            </div>
            <button onClick={copyCode} data-testid="copy-room-code" className="px-3 py-2 rounded text-xs bg-amber-400/15 text-amber-300 border border-amber-400/40 hover:bg-amber-400/25 flex items-center gap-1.5">
              <Copy size={12} /> Copy
            </button>
          </div>
        )}

        {!isActive && (
          <div className="mb-4 p-3 rounded-md bg-yellow-400/10 border border-yellow-400/30 text-yellow-200 text-sm">
            Phiên đã {s.status === "ended" ? "kết thúc" : "hủy"} — chỉ xem.
          </div>
        )}

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {players.map((p, i) => (
            <div key={p.player_id} className="rounded-xl border p-4 relative overflow-hidden"
              style={{ borderColor: `${p.color}66`, background: `linear-gradient(180deg, ${p.color}18 0%, #0c0d14 60%)` }}
              data-testid={`player-card-${p.player_id}`}>
              {p.current_score === maxScore && players.length > 1 && p.current_score > 0 && <div className="absolute top-2 right-2"><Trophy size={14} className="text-yellow-400" /></div>}
              <div className="flex items-center gap-3 mb-3">
                <img src={p.avatar} alt="" className="w-10 h-10 rounded-full border-2" style={{ borderColor: p.color }} />
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold text-sm truncate">{p.nickname}</div>
                  <div className="text-[10px] uppercase tracking-wider text-white/40">#{i + 1}</div>
                </div>
              </div>
              <div className={`font-display font-black text-5xl text-center mb-3 ${p.current_score < 0 ? "text-red-400" : ""}`} style={{ color: p.current_score < 0 ? "#f87171" : p.color }}>
                {p.current_score}
              </div>
              {isActive && canScore && (
                <>
                  <div className="grid grid-cols-3 gap-1.5 mb-1.5">
                    {[-10, -5, -1].map((d) => (
                      <button key={d} onClick={() => score(p.player_id, d)} data-testid={`score-${p.player_id}-${d}`}
                        className="py-2 rounded text-xs font-display font-bold bg-red-500/10 text-red-300 hover:bg-red-500/20 border border-red-500/20">
                        {d}
                      </button>
                    ))}
                  </div>
                  <div className="grid grid-cols-3 gap-1.5">
                    {[1, 5, 10].map((d) => (
                      <button key={d} onClick={() => score(p.player_id, d)} data-testid={`score-${p.player_id}-+${d}`}
                        className="py-2 rounded text-xs font-display font-bold bg-amber-400/15 text-amber-300 hover:bg-amber-400/25 border border-amber-400/30">
                        +{d}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
